/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import {
  UserFields,
  AccountLicenseTier,
  CustomFieldType,
  Organisation,
  CredentialType,
  AccountMembershipType
} from '@/store/types';

export interface AuthStoreState {
  signupError: string;
  signupData: SignupData;
  signupSuccess: boolean;
  loggedIn: boolean;
  loggedInUser: UserInfo;
  loggedInAccount: AccountInfo;
  showNotHomeAccountBar: boolean;
  authenticationError: ThrownAuthenticationError;
}

export interface UserLoginType {
  username: string;
  password: string;
}

export interface SignupData {
  emailAddress: string;
  firstName: string;
  lastName: string;
  password: string;
  organisationName: string;
  vatNumber: string;
  phoneNumber: string;
  city: string;
  countryCode: string;
  registrationUrl: string;
  acceptedTermsAndConditions: boolean;
}

export interface LoginResponse {
  // oidc response
  totpEnforced?: true;
  totpRequired?: true;
  token?: string;
  url?: string;

  // legacy response
  refresh?: string;
  access?: string;
}

interface TotpCode {
  otp: string;
  recoveryCode?: never;
}

interface RecoveryCode {
  otp?: never;
  recoveryCode: string;
}

export type OtpLoginData = (TotpCode | RecoveryCode) & {
  token: string;
  challenge: string;
};

export interface OtpInputData {
  code: string;
  withRecoveryCode: boolean;
  usePasswordInput?: boolean;
}

export class AuthenticationError extends Error {
  constructor(
    public statusCode: number,
    public message: string,
    public code: AuthenticationErrorCode,
    public data?: AuthenticationErrorData
  ) {
    super(message);
    this.name = this.constructor.name;
  }
}

export enum AuthenticationErrorCode {
  NOT_CONFIRMED = 'NOT_CONFIRMED',
  NOT_APPROVED = 'NOT_APPROVED',
  LOCKED = 'LOCKED',
  TEMPORARILY_LOCKED = 'TEMPORARILY_LOCKED',
  WRONG_CREDENTIALS = 'WRONG_CREDENTIALS',
  CHALLENGE_EXPIRED = 'CHALLENGE_EXPIRED',
  WRONG_OTP = 'WRONG_OTP',
  WRONG_RECOVERY_CODE = 'WRONG_RECOVERY_CODE',
  IDP_NOT_FOUND = 'IDP_NOT_FOUND',
  UNKNOWN_USER = 'UNKNOWN_USER',
  SUBJECT_MISMATCH = 'SUBJECT_MISMATCH',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  /** Attribute for general errors regarding authentication */
  ERROR = 'ERROR'
}

export interface AuthenticationErrorData extends Record<string, unknown> {
  timeout: string;
}

export class ConfirmationError extends Error {
  constructor(
    public errorCode: number,
    public message: string
  ) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

export interface FeatureFlags {
  advancedUpdateEnabled: boolean;
  apiEnabled: boolean;
  auditLogEnabled: boolean;
  autoUpdateServerEnabled: boolean;
  certificateManagementEnabled: boolean;
  cliTemplatesEnabled: boolean;
  customFieldsEnabled: boolean;
  updateJobsEnabled: boolean;
  updatePacketManagementEnabled: boolean;
  userManagementEnabled: boolean;
  isAdminAccount?: boolean;
  isHomeAccount?: boolean;
}

export interface AccountInfo {
  uuid: string;
  namespace: string;
  name: string;
  organisation: Organisation;
  internal: boolean;
  licenseTier: AccountLicenseTier;
  userFields: UserFields;
  customFields: CustomFieldType[];
  licensedFeatures: FeatureFlags;
}

export interface UserInfo {
  username: string;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  salutation: string;
  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  preferredLanguage: string;
  registeredAt: string;
  confirmed: boolean;
  organisation: Organisation;
  credential: CredentialType;
  accountMemberships: AccountMembershipType[];
  phoneNumber: string;
  totpEnabled: boolean;
  ssoRequired: boolean;
}

/**
 * An interface containing all errors that can occur during login
 */
export interface ThrownAuthenticationError {
  status: number | null;
  code: AuthenticationErrorCode | null;
  message: string;
}
